import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-dropdown';
import { twMerge } from 'tailwind-merge';

import { ChevronUp, ChevronDown } from '@videoblocks/react-icons';

import ArrowIcon from '../../filters/ArrowIcon';
import {
  ContentTypes,
  ContentTypesWithSnapshots,
} from '../NavSearchElements/ContentTypes';

import '../NavSearchElements/navSearchDropdown.css';

/**
 * `text` renders a bare label (top nav); `dark`/`light` render pills whose
 * sizing and colors vary with `size`. Resolved here in one place so the JSX
 * doesn't interleave the variants.
 */
function dropdownButtonClasses(
  variant: 'dark' | 'light' | 'text',
  size: 'default' | 'compact'
): string {
  if (variant === 'text') {
    // rounded-lg only shapes the focus ring — the background is transparent.
    return 'h-8 m-0 px-1 rounded-lg text-base bg-transparent text-gray-1000 hover:text-gray-900';
  }

  const pill = 'rounded-full border border-solid';
  const sizing =
    size === 'compact' ? 'h-8 m-0 py-2 px-4 text-sm' : 'h-14 m-2 p-4 text-base';
  const colors =
    variant === 'light'
      ? 'bg-white hover:bg-gray-100 text-gray-800 border-gray-400'
      : size === 'compact'
      ? 'bg-gray-800 hover:bg-gray-900 text-white border-gray-800'
      : 'bg-gray-800 hover:bg-gray-900 text-white';

  return twMerge(pill, sizing, colors);
}

export default function ProminentSearchDropdown({
  onChange = _.noop,
  value = '',
  useNewSearchUI,
  useSnapshots,
  clearSuggestions = _.noop,
  variant = 'dark',
  size = 'default',
}: {
  onChange: (value: string) => void;
  value: string;
  useNewSearchUI?: boolean;
  useSnapshots?: boolean;
  clearSuggestions: () => void;
  variant?: 'dark' | 'light' | 'text';
  size?: 'default' | 'compact';
}) {
  const isText = variant === 'text';
  const dropdownButtonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const handleFocusOut = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.relatedTarget)
    ) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    const button = dropdownRef.current;
    if (button) {
      button.addEventListener('focusout', handleFocusOut);
    }
    return () => {
      if (button) {
        button.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, []);

  const handleChange = (event) => {
    const contentType = event.value;
    onChange(contentType);
    setIsOpen(false);
  };

  const handleOptionSelect = (value: string) => {
    onChange(value);
    clearSuggestions();
    dropdownButtonRef.current.focus();
    setIsOpen(false);
  };

  const dropdownClick = () => {
    setIsOpen((prev) => !prev);
    dropdownButtonRef.current.focus();
  };

  const options = useSnapshots ? ContentTypesWithSnapshots : ContentTypes;

  if (useNewSearchUI) {
    return (
      <div id="search-dropdown-container" ref={dropdownRef}>
        <button
          className={twMerge(
            // focus:ring-inset keeps the focus ring inside the pill so it is
            // not clipped by the parent search-bar form (h-12) when the pill
            // (h-8) is centered with only a few px of breathing room.
            'flex justify-center items-center z-10 whitespace-nowrap font-semibold focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500',
            dropdownButtonClasses(variant, size)
          )}
          onClick={(e) => {
            e.preventDefault();
            dropdownClick();
          }}
          onFocus={clearSuggestions}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          ref={dropdownButtonRef}
          role="combobox"
          aria-controls="search-dropdown-options-container"
          aria-expanded={isOpen}
          aria-labelledby="search-dropdown-label-from-selection"
          type="button"
        >
          <span
            className={twMerge(
              'flex-grow flex items-center',
              size === 'compact'
                ? 'gap-2 px-0'
                : 'gap-2.5 pt-2 pr-0.5 pb-2 pl-2.5'
            )}
          >
            <span
              id="search-dropdown-label-from-selection"
              className="flex-grow text-overflow-ellipsis whitespace-nowrap"
              aria-label={`Content Type for Search: ${value}`}
            >
              {options.find((option) => option.value === value)?.label}
            </span>
            <ArrowIcon
              isCollapsed={!isOpen}
              invalidSelectionMessage={''}
              className={twMerge(
                isText || size !== 'compact' ? 'h-4 w-4' : 'h-3 w-3',
                variant === 'light' || isText ? 'fill-gray-800' : 'fill-white'
              )}
            />
          </span>
        </button>
        {isOpen && (
          <ul
            className="absolute min-w-3xs block transform bg-white border rounded-3xl shadow-md z-50 border-1 border-gray-400 border-solid py-4 mt-2.5 text-sm text-gray-900 overflow-hidden"
            role="listbox"
            id="search-dropdown-options-container"
            style={{ minWidth: '210px' }}
            aria-label="Content Type Options"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <li
                  className={`py-1.5 px-6 box-border cursor-pointer hover:bg-blue-100`}
                  key={`search-dropdown-option-${index}`}
                  id={`search-dropdown-option-${option.value}`}
                  role="option"
                  tabIndex={0}
                  aria-selected={isSelected}
                  onClick={() => {
                    handleOptionSelect(option.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsOpen(false);
                      dropdownButtonRef.current.focus();
                    } else if (e.key === 'Enter' || e.key === ' ') {
                      handleOptionSelect(option.value);
                      e.preventDefault();
                    }
                  }}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <span
      className={
        'unifiedNav-searchDropdown unifiedNav-searchDropdown-experiment'
      }
      data-cy="unifiedNav-searchDropdown"
    >
      <Dropdown
        aria-label="Select Search Type"
        onChange={handleChange}
        options={options}
        placeholder="Select..."
        value={value}
        arrowClosed={<ChevronDown className="w-4 h-4 m-1.5" />}
        arrowOpen={<ChevronUp className="w-4 h-4 m-1.5" />}
      />
    </span>
  );
}
